import React, { createContext, useContext, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { parseMessage } from '../utils/markdown';
import { useTelegramHaptic } from '../hooks/useTelegramHaptic';

const SnackbarContext = createContext();

export const SnackbarProvider = ({ children }) => {
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info',
        duration: 6000,
    });
    const { notificationOccurred } = useTelegramHaptic();

    const showSnackbar = (message, severity = 'info', duration = 6000) => {
        const messageStr = message ? String(message) : '';
        
        // Calculate duration based on message length for long messages
        const calculatedDuration = duration || Math.max(6000, messageStr.length * 50);
        
        setSnackbar({ 
            open: true, 
            message: messageStr, 
            severity,
            duration: calculatedDuration
        });
        
        // Add haptic feedback based on severity
        if (severity === 'success') {
            notificationOccurred('success');
        } else if (severity === 'error' || severity === 'warning') {
            notificationOccurred('error');
        }
    };

    const handleClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <SnackbarContext.Provider value={{ showSnackbar }}>
            {children}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={snackbar.duration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{
                    zIndex: 99999, // Highest z-index to be above everything
                    '& .MuiSnackbarContent-root': {
                        maxWidth: '90vw',
                    }
                }}
            >
                <Alert 
                    onClose={handleClose} 
                    severity={snackbar.severity} 
                    sx={{ 
                        width: '100%',
                        maxWidth: '600px',
                        '& .MuiAlert-message': {
                            maxHeight: 'none',
                            overflow: 'visible',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            width: '100%',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiAlert-icon': {
                            alignItems: 'center',
                            paddingTop: '0',
                        },
                        '& .MuiAlert-action': {
                            alignItems: 'center',
                            paddingTop: '0',
                        }
                    }}
                >
                    <span dangerouslySetInnerHTML={{ __html: parseMessage(snackbar.message) }} />
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};

export const useSnackbar = () => useContext(SnackbarContext);